<script setup lang="ts">
/**
 * Root layout dispatcher. Picks a layout component from `route.meta.layout`:
 *   - 'marketing' → MarketingLayout (public nav + footer)
 *   - 'bare'      → BareLayout (centered canvas, e.g. /login)
 *   - 'app'       → AppLayout (sidebar + topbar; session-gated by guard)
 *
 * Until the router has resolved its first navigation, `route.meta` is empty.
 * We render a blank canvas during that window so the app chrome never flashes
 * on top of a public marketing route.
 */
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAuthStore } from './stores/auth';
import AppLayout from './layouts/AppLayout.vue';
import MarketingLayout from './layouts/MarketingLayout.vue';
import BareLayout from './layouts/BareLayout.vue';

const route = useRoute();
const router = useRouter();
const auth = useAuthStore();
const ready = ref(false);

void router.isReady().then(() => {
  ready.value = true;
});

const layout = computed(() => {
  switch (route.meta.layout) {
    case 'marketing':
      return MarketingLayout;
    case 'bare':
      return BareLayout;
    case 'app':
      return AppLayout;
    default:
      return BareLayout;
  }
});

onMounted(async () => {
  if (!auth.ready) await auth.bootstrap();
});
</script>

<template>
  <component :is="layout" v-if="ready">
    <router-view v-slot="{ Component }">
      <component :is="Component" />
    </router-view>
  </component>
  <div v-else class="min-h-screen bg-canvas" aria-hidden="true" />
</template>
